import React, { useEffect, useMemo, useState } from "react";
import { APP_PAGE_BACKGROUND, BRAND_COLORS } from "./branding";
import { MapPane } from "./Map";
import { ELECTION_MAP_STANDARD_V1_KEY } from "./mapRenderingStandard";
import PointLayer, { getPointKindStyle } from "./PointLayer";

const PAGE_SIZE = 25;
const ALL_CATEGORY = "all";
const TYPE_ORDER = ["zs", "ms", "ds", "other"];
const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };
const TYPE_LABELS = {
  zs: "ZŠ",
  ms: "MŠ",
  ds: "DS",
  other: "Ostatní",
};

const DEFAULT_SORT = {
  key: "amount_kc",
  direction: "desc",
};

const pageStyles = {
  page: {
    minHeight: "100vh",
    background: APP_PAGE_BACKGROUND,
    padding: "24px 0 48px",
  },
  container: {
    width: "min(1180px, calc(100% - 32px))",
    margin: "0 auto",
    display: "grid",
    gap: 16,
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  },
  titleWrap: {
    display: "grid",
    gap: 4,
  },
  title: {
    margin: 0,
    color: BRAND_COLORS.primaryDarkBlue,
    fontSize: 28,
    lineHeight: 1.1,
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    fontSize: 14,
  },
  panel: {
    border: "1px solid #d9e2ec",
    borderRadius: 8,
    background: "white",
    padding: 16,
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
  },
  filterPanel: {
    border: "1px solid #d9e2ec",
    borderRadius: 8,
    background: "rgba(255, 255, 255, 0.96)",
    padding: 16,
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.06)",
    display: "grid",
    gap: 14,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 14,
    alignItems: "end",
  },
  filterGroup: {
    display: "grid",
    gap: 6,
    minWidth: 0,
  },
  filterLabel: {
    color: "#475569",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  rangeRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 8,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "white",
    color: "#0f172a",
    minHeight: 34,
    padding: "7px 10px",
    fontSize: 13,
  },
  typeOptions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  typeOption: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "#f8fafc",
    padding: "8px 10px",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 700,
  },
  filterMeta: {
    color: "#64748b",
    fontSize: 13,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  stat: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 12,
    display: "grid",
    gap: 4,
    background: "#ffffff",
  },
  statLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
  },
  statValue: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 900,
  },
  state: {
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.55,
  },
  info: {
    border: "1px solid #bfdbfe",
    borderRadius: 8,
    background: "#eff6ff",
    color: "#1e3a8a",
    padding: 12,
    fontSize: 13,
  },
  mapHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  mapMeta: {
    color: "#64748b",
    fontSize: 13,
  },
  mapLegend: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 12,
  },
  mapLegendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "#334155",
    fontSize: 13,
    fontWeight: 700,
  },
  mapLegendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 999,
    border: "2px solid white",
    boxShadow: "0 0 0 1px #cbd5e1",
  },
  error: {
    border: "1px solid #fecaca",
    borderRadius: 8,
    background: "#fef2f2",
    color: "#991b1b",
    padding: 12,
    fontSize: 14,
  },
  chartGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 16,
  },
  sectionTitle: {
    margin: "0 0 12px",
    color: "#0f172a",
    fontSize: 18,
    lineHeight: 1.25,
    fontWeight: 900,
  },
  chart: {
    display: "grid",
    gap: 10,
  },
  chartRow: {
    display: "grid",
    gridTemplateColumns: "minmax(92px, 0.8fr) minmax(120px, 2fr) minmax(84px, auto)",
    gap: 10,
    alignItems: "center",
    minWidth: 0,
  },
  chartLabel: {
    minWidth: 0,
    color: "#334155",
    fontSize: 13,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chartTrack: {
    height: 14,
    borderRadius: 999,
    background: "#edf2f7",
    overflow: "hidden",
  },
  chartFill: {
    height: "100%",
    borderRadius: 999,
    background: BRAND_COLORS.primaryBrightBlue,
  },
  chartValue: {
    textAlign: "right",
    color: "#0f172a",
    fontSize: 13,
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  tableHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  tableHeaderMeta: {
    color: "#64748b",
    fontSize: 13,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    textAlign: "left",
    color: "#475569",
    borderBottom: "1px solid #e2e8f0",
    padding: "10px 8px",
    whiteSpace: "nowrap",
  },
  sortButton: {
    border: 0,
    background: "transparent",
    color: "inherit",
    font: "inherit",
    fontWeight: 800,
    padding: 0,
    cursor: "pointer",
  },
  td: {
    borderBottom: "1px solid #f1f5f9",
    padding: "10px 8px",
    verticalAlign: "top",
  },
  amountCell: {
    borderBottom: "1px solid #f1f5f9",
    padding: "10px 8px",
    verticalAlign: "top",
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    fontWeight: 700,
  },
  pagination: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 12,
  },
  pageButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    background: "white",
    color: "#0f172a",
    padding: "8px 11px",
    fontWeight: 700,
    cursor: "pointer",
  },
  pageButtonDisabled: {
    color: "#94a3b8",
    cursor: "not-allowed",
    background: "#f8fafc",
  },
};

function formatKc(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  return `${new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(amount)} Kč`;
}

function formatCompactKc(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";
  const absolute = Math.abs(amount);
  const formatter = new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 1 });
  if (absolute >= 1_000_000_000) return `${formatter.format(amount / 1_000_000_000)} mld. Kč`;
  if (absolute >= 1_000_000) return `${formatter.format(amount / 1_000_000)} mil. Kč`;
  if (absolute >= 1_000) return `${formatter.format(amount / 1_000)} tis. Kč`;
  return formatKc(amount);
}

function formatCount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0";
  return new Intl.NumberFormat("cs-CZ", { maximumFractionDigits: 0 }).format(amount);
}

function normalizeSearch(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function getNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function hasValidSchoolCoordinates(school) {
  return (
    school &&
    school.geocode_status === "ok" &&
    school.lat !== null &&
    school.lat !== undefined &&
    school.lng !== null &&
    school.lng !== undefined &&
    Number.isFinite(Number(school.lat)) &&
    Number.isFinite(Number(school.lng))
  );
}

function getTypeLabel(type, school) {
  return school && school.type_label ? String(school.type_label) : TYPE_LABELS[type] || "-";
}

function getSchoolDisplayName(school) {
  return school && school.name ? String(school.name) : "Neznámá škola";
}

function getYears(rows) {
  const years = new Set();
  for (const row of rows) {
    if (Number.isInteger(row.year)) years.add(row.year);
  }
  return Array.from(years).sort((a, b) => a - b);
}

function compareStrings(a, b) {
  return String(a || "").localeCompare(String(b || ""), "cs-CZ", { sensitivity: "base" });
}

function sortRows(rows, sortState) {
  const direction = sortState.direction === "asc" ? 1 : -1;
  return rows.slice().sort((a, b) => {
    let result = 0;
    if (sortState.key === "amount_kc" || sortState.key === "year") {
      const av = getNumber(a[sortState.key]);
      const bv = getNumber(b[sortState.key]);
      result = (av == null ? Number.NEGATIVE_INFINITY : av) - (bv == null ? Number.NEGATIVE_INFINITY : bv);
    } else {
      result = compareStrings(a[sortState.key], b[sortState.key]);
    }
    if (result !== 0) return result * direction;
    return compareStrings(a.school_name, b.school_name) || compareStrings(a.investment_id, b.investment_id);
  });
}

function buildInvestmentRows(investments, schoolsById) {
  return investments.map((investment, index) => {
    const school = schoolsById.get(String(investment && investment.school_id)) || null;
    const schoolName = getSchoolDisplayName(school);
    const aliases = school && Array.isArray(school.name_aliases) ? school.name_aliases.map(String) : [];
    const type = school && school.type ? String(school.type) : "";
    const amount = getNumber(investment && investment.amount_kc);
    return {
      investment_id: String((investment && investment.investment_id) || `row:${index}`),
      school_id: String((investment && investment.school_id) || ""),
      school_name: schoolName,
      school_aliases: aliases,
      type,
      type_label: getTypeLabel(type, school),
      year: Number(investment && investment.year),
      category_new: String((investment && investment.category_new) || ""),
      activity: String((investment && investment.activity) || ""),
      amount_kc: amount,
    };
  });
}

function buildFilterOptions(rows) {
  const years = getYears(rows);
  const categories = Array.from(new Set(rows.map((row) => row.category_new).filter(Boolean))).sort(compareStrings);
  const typeMap = new Map();
  for (const row of rows) {
    if (!row.type) continue;
    if (!typeMap.has(row.type)) {
      typeMap.set(row.type, {
        type: row.type,
        label: row.type_label || TYPE_LABELS[row.type] || row.type,
      });
    }
  }
  const types = Array.from(typeMap.values()).sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a.type);
    const bi = TYPE_ORDER.indexOf(b.type);
    if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    return compareStrings(a.label, b.label);
  });
  return { years, categories, types };
}

function filterRows(rows, filters) {
  const query = normalizeSearch(filters.query);
  const selectedTypes = new Set(filters.types || []);
  const yearFrom = Number(filters.yearFrom);
  const yearTo = Number(filters.yearTo);

  return rows.filter((row) => {
    if (Number.isInteger(yearFrom) && row.year < yearFrom) return false;
    if (Number.isInteger(yearTo) && row.year > yearTo) return false;
    if (row.type && selectedTypes.size > 0 && !selectedTypes.has(row.type)) return false;
    if (filters.category !== ALL_CATEGORY && row.category_new !== filters.category) return false;
    if (query) {
      const searchText = normalizeSearch([row.school_name, ...row.school_aliases, row.activity].join(" "));
      if (!searchText.includes(query)) return false;
    }
    return true;
  });
}

function summarizeRows(rows) {
  const schoolIds = new Set();
  let totalAmountKc = 0;
  let investmentCount = 0;

  for (const row of rows) {
    investmentCount += 1;
    if (row.school_id) schoolIds.add(row.school_id);
    if (Number.isFinite(row.amount_kc)) totalAmountKc += row.amount_kc;
  }

  return {
    totalAmountKc,
    investmentCount,
    schoolCount: schoolIds.size,
  };
}

function aggregateRows(rows, keyFn, labelFn, extraFn) {
  const map = new Map();
  for (const row of rows) {
    const key = String(keyFn(row));
    if (!key) continue;
    let item = map.get(key);
    if (!item) {
      item = {
        key,
        label: labelFn(row),
        investment_count: 0,
        total_amount_kc: 0,
        schoolIds: new Set(),
        ...(typeof extraFn === "function" ? extraFn(row) : null),
      };
      map.set(key, item);
    }
    item.investment_count += 1;
    if (Number.isFinite(row.amount_kc)) item.total_amount_kc += row.amount_kc;
    if (row.school_id) item.schoolIds.add(row.school_id);
  }

  return Array.from(map.values()).map((item) => {
    const schoolCount = item.schoolIds.size;
    const { schoolIds, ...rest } = item;
    return {
      ...rest,
      school_count: schoolCount,
    };
  });
}

function buildAggregations(rows) {
  const byYear = aggregateRows(rows, (row) => row.year, (row) => String(row.year)).sort(
    (a, b) => Number(a.key) - Number(b.key)
  );
  const byCategory = aggregateRows(rows, (row) => row.category_new, (row) => row.category_new)
    .sort((a, b) => b.total_amount_kc - a.total_amount_kc || compareStrings(a.label, b.label))
    .slice(0, 8);
  const bySchool = aggregateRows(
    rows,
    (row) => row.school_id || row.school_name,
    (row) => row.school_name,
    (row) => ({
      school_id: row.school_id,
      school_name: row.school_name,
      type: row.type,
      type_label: row.type_label,
    })
  ).sort((a, b) => b.total_amount_kc - a.total_amount_kc || compareStrings(a.school_name, b.school_name));
  const byType = aggregateRows(
    rows,
    (row) => row.type || "unknown",
    (row) => row.type_label || "-",
    (row) => ({
      type: row.type,
      type_label: row.type_label,
    })
  ).sort((a, b) => b.total_amount_kc - a.total_amount_kc || compareStrings(a.label, b.label));

  return {
    byYear,
    byCategory,
    bySchool,
    byType,
  };
}

function buildCoordinateStats(schools) {
  const totalSchools = Array.isArray(schools) ? schools.length : 0;
  const mappedSchools = (Array.isArray(schools) ? schools : []).filter(hasValidSchoolCoordinates).length;
  return {
    totalSchools,
    mappedSchools,
    unmappedSchools: Math.max(0, totalSchools - mappedSchools),
  };
}

function buildSchoolMapAggregates(rows, schoolsById) {
  const map = new Map();
  for (const row of rows) {
    const school = schoolsById.get(row.school_id);
    if (!school || !hasValidSchoolCoordinates(school)) continue;
    let item = map.get(row.school_id);
    if (!item) {
      item = {
        school,
        school_id: row.school_id,
        investment_count: 0,
        total_amount_kc: 0,
        activities: [],
      };
      map.set(row.school_id, item);
    }
    item.investment_count += 1;
    if (Number.isFinite(row.amount_kc)) item.total_amount_kc += row.amount_kc;
    item.activities.push({
      activity: row.activity,
      amount_kc: Number.isFinite(row.amount_kc) ? row.amount_kc : 0,
    });
  }
  return Array.from(map.values()).sort((a, b) => b.total_amount_kc - a.total_amount_kc || compareStrings(a.school.name, b.school.name));
}

function buildEducationMarkers(schoolMapAggregates) {
  return schoolMapAggregates.map((item) => {
    const school = item.school;
    const type = school.type || "other";
    const typeLabel = getTypeLabel(type, school);
    const amountKc = item.total_amount_kc;
    const topActivities = item.activities
      .slice()
      .sort((a, b) => b.amount_kc - a.amount_kc || compareStrings(a.activity, b.activity))
      .slice(0, 3)
      .map((activity) => activity.activity)
      .filter(Boolean);

    return {
      id: item.school_id,
      lat: Number(school.lat),
      lng: Number(school.lng),
      kind: type,
      kindLabel: typeLabel,
      label: getSchoolDisplayName(school),
      value: amountKc,
      count: item.investment_count,
      tooltip: {
        title: getSchoolDisplayName(school),
        subtitle: `${typeLabel} · ${school.quarter || "-"}`,
        valueLabel: formatCompactKc(amountKc),
      },
      popup: {
        title: getSchoolDisplayName(school),
        typeLabel,
        address: school.address || "",
        quarter: school.quarter || "",
        amountKc,
        amountExactLabel: formatKc(amountKc),
        amountCompactLabel: formatCompactKc(amountKc),
        investmentCount: item.investment_count,
        topActivities,
      },
    };
  });
}

function clampPage(page, totalPages) {
  return Math.min(Math.max(1, page), Math.max(1, totalPages));
}

function EducationEmptyState({ children }) {
  return <div style={pageStyles.state}>{children}</div>;
}

function EducationFilterPanel({ filters, options, filteredCount, totalCount, onFilterChange }) {
  const { years, categories, types } = options;

  return (
    <section style={pageStyles.filterPanel} aria-label="Filtry školských investic">
      <div style={pageStyles.filterGrid}>
        <div style={pageStyles.filterGroup}>
          <label style={pageStyles.filterLabel}>Rok</label>
          <div style={pageStyles.rangeRow}>
            <select
              aria-label="Rok od"
              style={pageStyles.input}
              value={filters.yearFrom}
              onChange={(event) => onFilterChange({ yearFrom: Number(event.target.value) })}
            >
              {years.map((year) => (
                <option key={`from-${year}`} value={year}>
                  Od {year}
                </option>
              ))}
            </select>
            <select
              aria-label="Rok do"
              style={pageStyles.input}
              value={filters.yearTo}
              onChange={(event) => onFilterChange({ yearTo: Number(event.target.value) })}
            >
              {years.map((year) => (
                <option key={`to-${year}`} value={year}>
                  Do {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={pageStyles.filterGroup}>
          <label style={pageStyles.filterLabel}>Typ zařízení</label>
          <div style={pageStyles.typeOptions}>
            {types.map((item) => (
              <label key={item.type} style={pageStyles.typeOption}>
                <input
                  type="checkbox"
                  checked={filters.types.includes(item.type)}
                  onChange={(event) => {
                    const nextTypes = event.target.checked
                      ? Array.from(new Set([...filters.types, item.type]))
                      : filters.types.filter((type) => type !== item.type);
                    onFilterChange({ types: nextTypes });
                  }}
                />
                {item.label}
              </label>
            ))}
          </div>
        </div>

        <div style={pageStyles.filterGroup}>
          <label htmlFor="education-category-filter" style={pageStyles.filterLabel}>
            Kategorie
          </label>
          <select
            id="education-category-filter"
            style={pageStyles.input}
            value={filters.category}
            onChange={(event) => onFilterChange({ category: event.target.value })}
          >
            <option value={ALL_CATEGORY}>Všechny kategorie</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div style={pageStyles.filterGroup}>
          <label htmlFor="education-query-filter" style={pageStyles.filterLabel}>
            Hledat
          </label>
          <input
            id="education-query-filter"
            style={pageStyles.input}
            value={filters.query}
            onChange={(event) => onFilterChange({ query: event.target.value })}
            placeholder="Škola nebo aktivita"
          />
        </div>
      </div>
      <div style={pageStyles.filterMeta}>
        Zobrazeno {formatCount(filteredCount)} z {formatCount(totalCount)} investic.
      </div>
    </section>
  );
}

function EducationKpiGrid({ summary }) {
  return (
    <section style={pageStyles.panel} aria-label="Souhrnné metriky">
      <div style={pageStyles.statsGrid}>
        <div style={pageStyles.stat}>
          <span style={pageStyles.statLabel}>Celkem</span>
          <strong style={pageStyles.statValue} title={formatKc(summary.totalAmountKc)}>
            {formatCompactKc(summary.totalAmountKc)}
          </strong>
        </div>
        <div style={pageStyles.stat}>
          <span style={pageStyles.statLabel}>Investice</span>
          <strong style={pageStyles.statValue}>{formatCount(summary.investmentCount)}</strong>
        </div>
        <div style={pageStyles.stat}>
          <span style={pageStyles.statLabel}>Zařízení</span>
          <strong style={pageStyles.statValue}>{formatCount(summary.schoolCount)}</strong>
        </div>
      </div>
    </section>
  );
}

function EducationBarChart({ title, rows, emptyLabel }) {
  const maxAmount = rows.reduce((max, row) => Math.max(max, Number(row.total_amount_kc) || 0), 0);

  return (
    <section style={pageStyles.panel}>
      <h2 style={pageStyles.sectionTitle}>{title}</h2>
      {rows.length === 0 ? (
        <EducationEmptyState>{emptyLabel}</EducationEmptyState>
      ) : (
        <div style={pageStyles.chart}>
          {rows.map((row) => {
            const amount = Number(row.total_amount_kc) || 0;
            const width = maxAmount > 0 ? Math.max(4, (amount / maxAmount) * 100) : 4;
            return (
              <div key={row.key} style={pageStyles.chartRow}>
                <span style={pageStyles.chartLabel} title={row.label}>
                  {row.label}
                </span>
                <span style={pageStyles.chartTrack} aria-hidden="true">
                  <span style={{ ...pageStyles.chartFill, width: `${width}%` }} />
                </span>
                <span style={pageStyles.chartValue} title={formatKc(amount)}>
                  {formatCompactKc(amount)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function EducationTopSchools({ rows }) {
  return (
    <EducationBarChart
      title="Top školy podle částky"
      rows={rows.slice(0, 10).map((row) => ({
        key: row.key,
        label: row.school_name,
        total_amount_kc: row.total_amount_kc,
      }))}
      emptyLabel="Pro zvolené filtry nejsou dostupné žádné školy."
    />
  );
}

function EducationMapSection({ areaId, markers, coordinateStats }) {
  const [fitRequestKey, setFitRequestKey] = useState(`education:${areaId || "none"}`);
  useEffect(() => {
    setFitRequestKey(`education:${areaId || "none"}`);
  }, [areaId]);
  const unmappedCount = coordinateStats.unmappedSchools;
  const unmappedText =
    unmappedCount === 1
      ? "1 škola zatím nemá souřadnice a není v mapě."
      : unmappedCount >= 2 && unmappedCount <= 4
        ? `${formatCount(unmappedCount)} školy zatím nemají souřadnice a nejsou v mapě.`
        : `${formatCount(unmappedCount)} škol zatím nemá souřadnice a není v mapě.`;

  return (
    <section style={pageStyles.panel} aria-label="Mapa školských zařízení">
      <div style={pageStyles.mapHeader}>
        <h2 style={pageStyles.sectionTitle}>Mapa školských zařízení</h2>
        {coordinateStats.unmappedSchools > 0 ? <span style={pageStyles.mapMeta}>{unmappedText}</span> : null}
      </div>
      {markers.length === 0 ? (
        <EducationEmptyState>Pro zvolené filtry nejsou školy se souřadnicemi.</EducationEmptyState>
      ) : (
	        <MapPane
	          mapKey="education-schools"
	          districtsGeoJson={EMPTY_GEOJSON}
	          geoJsonInteractive={false}
	          mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
	          height="clamp(340px, 34vw, 520px)"
	          scrollWheelZoom={false}
	          onResetView={() => setFitRequestKey(`education:${areaId || "none"}:reset:${Date.now()}`)}
	        >
		          <PointLayer
		            markers={markers}
		            paneName="volby-education-school-markers"
		            fitBounds
		            fitBoundsRequestKey={fitRequestKey}
		          />
        </MapPane>
      )}
      <div style={pageStyles.mapLegend} aria-label="Legenda typů škol">
        {TYPE_ORDER.map((type) => {
          const style = getPointKindStyle(type);
          return (
            <span key={type} style={pageStyles.mapLegendItem}>
              <span style={{ ...pageStyles.mapLegendSwatch, background: style.color }} aria-hidden="true" />
              {TYPE_LABELS[type]}
            </span>
          );
        })}
      </div>
    </section>
  );
}

function SortHeader({ label, sortKey, sortState, onSort, alignRight = false }) {
  const isActive = sortState.key === sortKey;
  const suffix = isActive ? (sortState.direction === "asc" ? " ↑" : " ↓") : "";
  return (
    <th style={{ ...pageStyles.th, textAlign: alignRight ? "right" : "left" }}>
      <button type="button" style={pageStyles.sortButton} onClick={() => onSort(sortKey)}>
        {label}
        {suffix}
      </button>
    </th>
  );
}

function EducationInvestmentsTable({ rows, sortState, page, totalPages, onSort, onPageChange }) {
  const sortedRows = sortRows(rows, sortState);
  const safePage = clampPage(page, totalPages);
  const firstIndex = (safePage - 1) * PAGE_SIZE;
  const visibleRows = sortedRows.slice(firstIndex, firstIndex + PAGE_SIZE);

  return (
    <section style={pageStyles.panel}>
      <div style={pageStyles.tableHeader}>
        <h2 style={pageStyles.sectionTitle}>Investice</h2>
        <span style={pageStyles.tableHeaderMeta}>
          Strana {formatCount(safePage)} z {formatCount(totalPages)}
        </span>
      </div>
      {rows.length === 0 ? (
        <EducationEmptyState>Žádné investice neodpovídají filtrům.</EducationEmptyState>
      ) : (
        <>
          <div style={pageStyles.tableWrap}>
            <table style={pageStyles.table}>
              <thead>
                <tr>
                  <SortHeader label="Rok" sortKey="year" sortState={sortState} onSort={onSort} />
                  <SortHeader label="Zařízení" sortKey="school_name" sortState={sortState} onSort={onSort} />
                  <SortHeader label="Typ" sortKey="type_label" sortState={sortState} onSort={onSort} />
                  <SortHeader label="Kategorie" sortKey="category_new" sortState={sortState} onSort={onSort} />
                  <SortHeader label="Aktivita" sortKey="activity" sortState={sortState} onSort={onSort} />
                  <SortHeader label="Částka" sortKey="amount_kc" sortState={sortState} onSort={onSort} alignRight />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.investment_id}>
                    <td style={pageStyles.td}>{Number.isInteger(row.year) ? row.year : "-"}</td>
                    <td style={pageStyles.td}>{row.school_name}</td>
                    <td style={pageStyles.td}>{row.type_label}</td>
                    <td style={pageStyles.td}>{row.category_new}</td>
                    <td style={pageStyles.td}>{row.activity}</td>
                    <td style={pageStyles.amountCell}>{formatKc(row.amount_kc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={pageStyles.pagination}>
            <button
              type="button"
              style={{
                ...pageStyles.pageButton,
                ...(safePage <= 1 ? pageStyles.pageButtonDisabled : null),
              }}
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
            >
              Předchozí
            </button>
            <span style={pageStyles.tableHeaderMeta}>
              {formatCount(firstIndex + 1)}-{formatCount(Math.min(firstIndex + PAGE_SIZE, rows.length))} z{" "}
              {formatCount(rows.length)}
            </span>
            <button
              type="button"
              style={{
                ...pageStyles.pageButton,
                ...(safePage >= totalPages ? pageStyles.pageButtonDisabled : null),
              }}
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
            >
              Další
            </button>
          </div>
        </>
      )}
    </section>
  );
}

export default function EducationPage({ areaId }) {
  const [payload, setPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    yearFrom: "",
    yearTo: "",
    types: [],
    category: ALL_CATEGORY,
    query: "",
  });
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!areaId) {
      setPayload(null);
      setError("");
      setIsLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    setPayload(null);
    setError("");
    setIsLoading(true);

    (async () => {
      try {
        const response = await fetch(`/api/areas/${encodeURIComponent(String(areaId))}/education`, {
          credentials: "include",
          signal: ac.signal,
        });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          const message =
            data && typeof data.error === "string" && data.error
              ? data.error
              : `Nepodařilo se načíst školská data (${response.status}).`;
          throw new Error(message);
        }
        if (ac.signal.aborted) return;
        setPayload(data && typeof data === "object" ? data : null);
      } catch (err) {
        if (err && err.name === "AbortError") return;
        setPayload(null);
        setError(err && err.message ? String(err.message) : "Nepodařilo se načíst školská data.");
      } finally {
        if (!ac.signal.aborted) setIsLoading(false);
      }
    })();

    return () => ac.abort();
  }, [areaId]);

  const schoolsById = useMemo(() => {
    const map = new Map();
    const schools = Array.isArray(payload && payload.schools) ? payload.schools : [];
    schools.forEach((school) => {
      if (school && school.school_id) map.set(String(school.school_id), school);
    });
    return map;
  }, [payload]);
  const schools = useMemo(() => (Array.isArray(payload && payload.schools) ? payload.schools : []), [payload]);

  const investments = useMemo(
    () => (Array.isArray(payload && payload.investments) ? payload.investments : []),
    [payload]
  );
  const investmentRows = useMemo(() => buildInvestmentRows(investments, schoolsById), [investments, schoolsById]);
  const filterOptions = useMemo(() => buildFilterOptions(investmentRows), [investmentRows]);

  useEffect(() => {
    if (!filterOptions.years.length) return;
    const minYear = filterOptions.years[0];
    const maxYear = filterOptions.years[filterOptions.years.length - 1];
    const availableTypes = filterOptions.types.map((item) => item.type);

    setFilters((current) => {
      const nextTypes = current.types.length ? current.types.filter((type) => availableTypes.includes(type)) : availableTypes;
      const nextYearFrom = current.yearFrom !== "" && Number.isInteger(Number(current.yearFrom)) ? Number(current.yearFrom) : minYear;
      const nextYearTo = current.yearTo !== "" && Number.isInteger(Number(current.yearTo)) ? Number(current.yearTo) : maxYear;
      const boundedFrom = Math.min(Math.max(nextYearFrom, minYear), maxYear);
      const boundedTo = Math.min(Math.max(nextYearTo, minYear), maxYear);
      const categoryExists = current.category === ALL_CATEGORY || filterOptions.categories.includes(current.category);

      return {
        ...current,
        yearFrom: Math.min(boundedFrom, boundedTo),
        yearTo: Math.max(boundedFrom, boundedTo),
        types: nextTypes,
        category: categoryExists ? current.category : ALL_CATEGORY,
      };
    });
  }, [filterOptions]);

  const filteredRows = useMemo(() => filterRows(investmentRows, filters), [investmentRows, filters]);
  const dashboardSummary = useMemo(() => summarizeRows(filteredRows), [filteredRows]);
  const aggregations = useMemo(() => buildAggregations(filteredRows), [filteredRows]);
  const coordinateStats = useMemo(() => buildCoordinateStats(schools), [schools]);
  const schoolMapAggregates = useMemo(
    () => buildSchoolMapAggregates(filteredRows, schoolsById),
    [filteredRows, schoolsById]
  );
  const educationMarkers = useMemo(() => buildEducationMarkers(schoolMapAggregates), [schoolMapAggregates]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const isEmpty = Boolean(payload && payload.is_empty) || (!isLoading && !error && investmentRows.length === 0);
  const hasDashboard = !isLoading && !error && !isEmpty;
  const summaryMissing = Boolean(payload && !payload.summary);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    setPage((current) => clampPage(current, totalPages));
  }, [totalPages]);

  function updateFilters(patch) {
    setFilters((current) => {
      const next = { ...current, ...patch };
      if (Number(next.yearFrom) > Number(next.yearTo)) {
        if (Object.prototype.hasOwnProperty.call(patch, "yearFrom")) next.yearTo = next.yearFrom;
        if (Object.prototype.hasOwnProperty.call(patch, "yearTo")) next.yearFrom = next.yearTo;
      }
      return next;
    });
  }

  function updateSort(sortKey) {
    setSortState((current) => {
      if (current.key !== sortKey) {
        return {
          key: sortKey,
          direction: sortKey === "amount_kc" ? "desc" : "asc",
        };
      }
      return {
        key: sortKey,
        direction: current.direction === "asc" ? "desc" : "asc",
      };
    });
  }

  return (
    <main style={pageStyles.page}>
      <div style={pageStyles.container}>
        {error ? <div style={pageStyles.error}>{error}</div> : null}

        <section style={pageStyles.panel}>
          {isLoading ? <EducationEmptyState>Načítám školská data...</EducationEmptyState> : null}
          {!isLoading && !error && isEmpty ? (
            <EducationEmptyState>Pro tuto oblast zatím nejsou připravená školská data.</EducationEmptyState>
          ) : null}
          {hasDashboard ? (
            <EducationEmptyState>
              Dashboard používá investiční řádky jako zdroj pravdy a přepočítává přehledy podle aktuálních filtrů.
            </EducationEmptyState>
          ) : null}
        </section>

        {hasDashboard ? (
          <>
            {summaryMissing ? (
              <div style={pageStyles.info}>Souhrnný cache soubor není dostupný; přehledy jsou spočítané z investic.</div>
            ) : null}
            <EducationFilterPanel
              filters={filters}
              options={filterOptions}
              filteredCount={filteredRows.length}
              totalCount={investmentRows.length}
              onFilterChange={updateFilters}
            />
            <EducationMapSection areaId={areaId} markers={educationMarkers} coordinateStats={coordinateStats} />
            <EducationKpiGrid summary={dashboardSummary} />
            <div style={pageStyles.chartGrid}>
              <EducationBarChart
                title="Investice podle let"
                rows={aggregations.byYear}
                emptyLabel="Pro zvolené filtry nejsou dostupné žádné roky."
              />
              <EducationBarChart
                title="Investice podle kategorií"
                rows={aggregations.byCategory}
                emptyLabel="Pro zvolené filtry nejsou dostupné žádné kategorie."
              />
              <EducationTopSchools rows={aggregations.bySchool} />
            </div>
            <EducationInvestmentsTable
              rows={filteredRows}
              sortState={sortState}
              page={page}
              totalPages={totalPages}
              onSort={updateSort}
              onPageChange={setPage}
            />
          </>
        ) : null}
      </div>
    </main>
  );
}
